<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import { settingCommonConfig } from './configs/common'
import { settingStyles } from './configs/styles'
import { api } from '@/api'
import { useLoadingStore, useToastStore, useUserStore } from '@/stores'
import { pageViewTrack, useElementClick } from '@/composables/tracks'
import { LOCALE_MAP, type LocaleCode, i18n, setLocale } from '@/i18n'

const config = settingCommonConfig

const { $toast } = useToastStore()
const { $showLoading, $hideLoading } = useLoadingStore()
const router = useRouter()

// 从环境变量获取链接
const userAgreementUrl = import.meta.env.VITE_USER_AGREEMENT_URL
const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL

const userStore = useUserStore()
const { elementClick } = useElementClick()

// 语言切换
const currentLocale = ref<LocaleCode>(i18n.locale as LocaleCode)
const languageModalVisible = ref(false)
const localeLabelMap: Record<LocaleCode, () => string> = {
  'en': () => config.texts.languageEnglish,
  'zh-TW': () => config.texts.languageTraditionalChinese,
  'ja': () => config.texts.languageJapanese,
  'ko': () => config.texts.languageKorean,
  'fr': () => config.texts.languageFrench,
  'de': () => config.texts.languageGerman,
  'es': () => config.texts.languageSpanish,
  'id': () => config.texts.languageBahasaIndonesia,
}
const localeOptions = Object.keys(localeLabelMap) as LocaleCode[]
function getLocaleLabel(locale: LocaleCode) {
  const getter = localeLabelMap[locale]
  return getter ? getter() : locale
}
const currentLocaleLabel = computed(() => {
  return getLocaleLabel(currentLocale.value)
})
function onClickLanguage() {
  languageModalVisible.value = true
}
function onSelectLanguage(locale: LocaleCode) {
  if (locale === currentLocale.value)
    return
  elementClick(`setting-language-${locale}`)
  setLocale(locale)
  currentLocale.value = locale
  languageModalVisible.value = false
  api.post(`/member/setting/language/set?language=${LOCALE_MAP[locale]}`).catch((error: any) => {
    console.error(error)
  })
}

// 注销账户
const unsubscribeVisible = ref(false)
const unsubscribeLoading = ref(false)
async function onUnsubscribe() {
  if (unsubscribeLoading.value)
    return
  unsubscribeLoading.value = true
  try {
    await api.post('/member/sign/out')
    $toast(config.texts.accountCancellationSuccess)
    unsubscribeVisible.value = false
    unsubscribeLoading.value = false
    localStorage.clear()
    sessionStorage.clear()
    router.replace({ name: 'member' })
  }
  catch (error: any) {
    console.error(error)
  }
}

function showLogOffDialog() {
  unsubscribeVisible.value = true
  pageViewTrack('account_Agreement')
}

// 自动解锁
const autoLockStatus = ref(userStore.$state.autoLock)
const autoLockLoading = ref(false)
async function onAutoLock() {
  if (autoLockLoading.value)
    return
  autoLockLoading.value = true

  elementClick(!autoLockStatus.value ? 'auto-unlock-on' : 'auto-unlock-off')
  try {
    await api.post('/member/update/auto-lock', {
      autoLock: !autoLockStatus.value,
    })
    autoLockStatus.value = !autoLockStatus.value
    $toast(config.texts.autoUnlockSuccess)
  }
  catch (error: any) {
    console.error(error)
  }
  finally {
    autoLockLoading.value = false
  }
}

const autoSubscriptionStatus = ref(userStore.$state.subscribeRenewal)
const subscriptionConfirmModal = ref(false)
async function onAutoSubscription(force = false) {
  if (autoSubscriptionStatus.value && !force) {
    subscriptionConfirmModal.value = true
    return
  }
  if (force)
    subscriptionConfirmModal.value = false

  if (autoLockLoading.value)
    return
  $showLoading()
  try {
    await api.post(`/member/subscribe/cancelSubscription?cancelAtPeriod=${(!autoSubscriptionStatus.value)}`)
    autoSubscriptionStatus.value = !autoSubscriptionStatus.value
    $toast(config.texts.autoSubscriptionRenewalSuccess)
  }
  catch (error: any) {
    console.error(error)
  }
  finally {
    $hideLoading()
  }
}
</script>

<template>
  <div class="cover bg-n-bg-global">
    <BasicNavbar class="color-n-text-2" />
    <div class="mt-48px mx-10px bg-n-bg-card b-rd-2" :class="[settingStyles.pageMarginX, settingStyles.cardPaddingX]">
      <div>
        <div
          class="w-full h-61px flex items-center justify-between border-b border-color-n-border-primary"
          @click="onClickLanguage"
        >
          <div class="flex items-center space-x-7px">
            <p class="text-15px font-bold text-n-text-1">
              {{ config.texts.language }}
            </p>
          </div>
          <div class="flex items-center space-x-4px">
            <span class="text-15px text-n-text-2">
              {{ currentLocaleLabel }}
            </span>
            <i-icon-park-outline-right class="text-n-text-3" />
          </div>
        </div>
      </div>
      <div>
        <a
          :href="userAgreementUrl"
          class="w-full h-61px flex items-center justify-between border-b border-color-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <p class="text-15px font-bold text-n-text-1">
              {{ config.texts.userAgreement }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </a>
      </div>
      <div>
        <a
          :href="privacyPolicyUrl"
          class="w-full h-61px flex items-center justify-between border-b border-color-n-border-primary"
        >
          <div class="flex items-center space-x-7px">
            <p class="text-15px font-bold text-n-text-1">
              {{ config.texts.privacyPolicy }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </a>
      </div>
      <div>
        <div
          class="w-full h-61px flex items-center justify-between border-b border-color-n-border-primary"
          @click="showLogOffDialog"
        >
          <div class="flex items-center space-x-7px">
            <p class="text-15px font-bold text-n-text-1">
              {{ config.texts.accountCancellation }}
            </p>
          </div>
          <i-icon-park-outline-right class="text-n-text-3" />
        </div>
      </div>
      <div>
        <div class="w-full h-61px flex items-center justify-between border-b border-color-n-border-primary">
          <div class="flex items-center space-x-7px">
            <p class="text-15px font-bold text-n-text-1">
              {{ config.texts.autoUnlock }}
            </p>
          </div>
          <BasicSwitch :is-open="autoLockStatus" @change="onAutoLock" />
        </div>
      </div>
      <div v-if="userStore.$state.subscribe">
        <div class="w-full h-61px flex items-center justify-between">
          <div class="flex items-center space-x-7px">
            <p class="text-15px font-bold text-n-text-1">
              {{ config.texts.autoSubscriptionRenewal }}
            </p>
          </div>
          <BasicSwitch :is-open="autoSubscriptionStatus" @change="onAutoSubscription" />
        </div>
      </div>
    </div>
    <BasicPopup v-model="unsubscribeVisible" :closable="false">
      <div class="px-20px bg-n-bg-card b-rd-t-2">
        <h3 class="pt-20px pb-15px text-18px text-center text-n-text-1">
          {{ config.texts.importantOperation }}
        </h3>
        <p class="text-n-text-2">
          {{ config.texts.accountCancellationConfirm }}
        </p>
        <div class="flex justify-center items-center space-x-20px pb-50px">
          <a
            class="block mt-4 w-100px h-9 lh-9 text-15px text-n-text-2 text-center bg-n-bg-card rounded-full border border-color-n-border-primary"
            @click="onUnsubscribe"
          >
            {{ config.texts.verify }}
          </a>
          <a
            class="block mt-4 w-100px h-9 lh-9 text-15px text-white text-center btn-solid rounded-full"
            @click="unsubscribeVisible = false"
          >
            {{ config.texts.cancel }}
          </a>
        </div>
      </div>
    </BasicPopup>

    <BasicModal v-model="languageModalVisible">
      <div class="p-5">
        <h3 class="mb-4 text-16px text-center text-n-text-1">
          {{ config.texts.language }}
        </h3>
        <div
          v-for="locale in localeOptions"
          :key="locale"
          class="h-44px flex items-center justify-between border-b border-color-n-border-primary px-2"
          @click="onSelectLanguage(locale)"
        >
          <span class="text-15px text-n-text-1">{{ getLocaleLabel(locale) }}</span>
          <i-ep-check v-if="locale === currentLocale" class="text-n-primary" />
        </div>
      </div>
    </BasicModal>

    <BasicModal v-model="subscriptionConfirmModal" :order="6">
      <div class="p-5">
        <div class="text-12px text-n-text-2">
          <p>{{ config.texts.cancelRenewalTip }}</p>
        </div>
        <div class="flex justify-center items-center space-x-20px">
          <a
            class="block mt-4 w-100px h-9 lh-9 text-15px text-n-text-2 text-center bg-n-bg-card rounded-full border border-color-n-border-primary"
            @click="subscriptionConfirmModal = false"
          >
            {{ config.texts.cancel }}
          </a>
          <a
            class="block mt-4 w-100px h-9 lh-9 text-15px text-white text-center btn-solid rounded-full"
            @click="onAutoSubscription(true)"
          >
            {{ config.texts.confirm }}
          </a>
        </div>
      </div>
    </BasicModal>
  </div>
</template>

<style lang="scss" scoped>
.cover {
  width: 100%;
  min-height: 100vh;
}
</style>
